/**
 * WebSocket сервис для real-time сообщений в чатах
 * Использует STOMP протокол поверх нативного WebSocket
 */

import { Client } from '@stomp/stompjs';

let stompClient = null;
let isConnected = false;
let reconnectAttempts = 0;
let reconnectTimer = null;
let activeUsers = 0;           // Счётчик компонентов, использующих WebSocket
let globalCallbacks = null;    // Глобальные колбэки (onMessage, onTyping)
let globalToken = null;        // Токен, с которым подключались
let subscriptions = [];        // Хранение активных подписок для восстановления после reconnect

const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_DELAY = 5000;

/**
 * Получение базового URL для WebSocket (ws:// / wss://)
 */
const getWsBaseUrl = () => {
    const apiUrl = process.env.REACT_APP_API_BASE_URL || 'https://api.hrnb.ru';
    const baseUrl = apiUrl.endsWith('/api') ? apiUrl.replace(/\/api$/, '') : apiUrl;
    return baseUrl.replace(/^http(s)?:\/\//, 'ws$1://');
};

/**
 * Создание нативного WebSocket с детальным логированием
 * Токен передаём в query-параметре (handshake) и через STOMP connectHeaders
 */
const createNativeWebSocket = (jwtToken) => {
    const wsUrl = jwtToken
        ? `${getWsBaseUrl()}/ws?token=${encodeURIComponent(jwtToken)}`
        : `${getWsBaseUrl()}/ws`;
    console.log('[WebSocket] Создание WebSocket:', wsUrl.replace(/token=[^&]+/, 'token=***'));

    let ws;
    try {
        ws = new WebSocket(wsUrl);
    } catch (err) {
        console.error('[WebSocket] Исключение при new WebSocket():', err);
        throw err;
    }

    console.log('[WebSocket] WebSocket создан, readyState:', ws.readyState, '(CONNECTING=0, OPEN=1, CLOSING=2, CLOSED=3)');

    ws.onerror = (event) => {
        console.error('[WebSocket] onerror — readyState:', ws.readyState, 'url:', wsUrl.replace(/token=[^&]+/, 'token=***'));
    };

    ws.onclose = (event) => {
        console.log(`[WebSocket] onclose — code: ${event.code}, reason: "${event.reason}", wasClean: ${event.wasClean}, readyState: ${ws.readyState}`);
    };

    return ws;
};

/**
 * Внутренняя функция создания и активации STOMP клиента.
 * НЕ трогает activeUsers — только создаёт подключение.
 */
const _doConnect = () => {
    const { getAccessToken: getToken } = require('./token');
    // Всегда получаем свежий токен при подключении/reconnect
    const jwtToken = getToken();

    if (!jwtToken) {
        console.error('[WebSocket] JWT токен не найден');
        if (globalCallbacks?.onMessage) {
            globalCallbacks.onMessage({ error: true, message: 'Токен авторизации не найден' });
        }
        return;
    }

    // Обновляем globalToken для консистентности
    globalToken = jwtToken;

    stompClient = new Client({
        webSocketFactory: () => createNativeWebSocket(jwtToken),
        connectHeaders: {
            Authorization: `Bearer ${jwtToken}`
        },
        debug: (str) => {
            console.debug('[WebSocket STOMP]', str);
        },
        reconnectDelay: 0,
        heartbeatIncoming: 45000,
        heartbeatOutgoing: 45000,
    });

    stompClient.onConnect = (frame) => {
        isConnected = true;
        reconnectAttempts = 0;
        console.log('[WebSocket] Подключено:', frame);

        // Восстанавливаем все подписки после reconnect
        _restoreSubscriptions();

        if (globalCallbacks?.onMessage) {
            globalCallbacks.onMessage({ connected: true, client: stompClient });
        }
    };

    stompClient.onDisconnect = (frame) => {
        console.log('[WebSocket] Disconnected:', frame);
        isConnected = false;
        _scheduleReconnect();
    };

    stompClient.onStompError = (frame) => {
        console.error('[WebSocket] STOMP error:', frame);
        isConnected = false;
        _scheduleReconnect();
    };

    stompClient.onWebSocketError = (error) => {
        console.error('[WebSocket] WebSocket error:', error.message || error);
        isConnected = false;
        _scheduleReconnect();
    };

    stompClient.activate();
};

/**
 * Восстановление подписок после reconnect
 */
const _restoreSubscriptions = () => {
    if (subscriptions.length === 0) return;

    console.log(`[WebSocket] Восстановление ${subscriptions.length} подписок после reconnect`);

    subscriptions.forEach(sub => {
        if (sub.type === 'chat') {
            const newSubscription = stompClient.subscribe(`/topic/chat.${sub.chatId}`, (message) => {
                try {
                    const data = JSON.parse(message.body);
                    sub.callback(data);
                } catch (error) {
                    console.error('[WebSocket] Ошибка парсинга сообщения при восстановлении:', error);
                }
            });
            sub.subscription = newSubscription;
        } else if (sub.type === 'typing') {
            const newSubscription = stompClient.subscribe(`/topic/chat.${sub.chatId}.typing`, (event) => {
                try {
                    const data = JSON.parse(event.body);
                    sub.callback(data);
                } catch (error) {
                    console.error('[WebSocket] Ошибка парсинга typing события при восстановлении:', error);
                }
            });
            sub.subscription = newSubscription;
        } else if (sub.type === 'presence') {
            const newSubscription = stompClient.subscribe(`/topic/presence.${sub.userId}`, (event) => {
                try {
                    const data = JSON.parse(event.body);
                    sub.callback(data);
                } catch (error) {
                    console.error('[WebSocket] Ошибка парсинга presence события при восстановлении:', error);
                }
            });
            sub.subscription = newSubscription;
        } else if (sub.type === 'polls') {
            const newSubscription = stompClient.subscribe(`/topic/chat.${sub.chatId}.polls`, (event) => {
                try {
                    const data = JSON.parse(event.body);
                    sub.callback(data);
                } catch (error) {
                    console.error('[WebSocket] Ошибка парсинга poll события при восстановлении:', error);
                }
            });
            sub.subscription = newSubscription;
        } else if (sub.type === 'notifications') {
            const newSubscription = stompClient.subscribe('/user/queue/notifications', (event) => {
                try {
                    const data = JSON.parse(event.body);
                    sub.callback(data);
                } catch (error) {
                    console.error('[WebSocket] Ошибка парсинга уведомления при восстановлении:', error);
                }
            });
            sub.subscription = newSubscription;
        }
        // При необходимости добавить другие типы подписок
    });

    console.log('[WebSocket] Подписки восстановлены');
};

/**
 * Регистрация подписки для последующего восстановления
 */
const _registerSubscription = (subscription, type, chatId = null, userId = null, callback = null) => {
    const subEntry = { subscription, type, chatId, userId, callback };
    subscriptions.push(subEntry);
    return subEntry;
};

/**
 * Отписка и удаление из реестра
 */
const _unregisterSubscription = (subscription) => {
    const index = subscriptions.findIndex(s => s.subscription === subscription);
    if (index !== -1) {
        subscriptions.splice(index, 1);
    }
};

/**
 * Внутренняя функция планирования reconnect.
 * НЕ увеличивает activeUsers — reconnect — это внутренняя логика.
 */
const _scheduleReconnect = () => {
    if (activeUsers === 0) {
        console.log('[WebSocket] Нет активных пользователей, переподключение отменено');
        return;
    }
    if (reconnectTimer) {
        return;
    }

    if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
        console.error('[WebSocket] Максимум попыток переподключения');
        if (globalCallbacks?.onMessage) {
            globalCallbacks.onMessage({ error: true, message: 'Не удалось подключиться к WebSocket' });
        }
        return;
    }

    reconnectAttempts++;
    console.log(`[WebSocket] Попытка переподключения ${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS} через ${RECONNECT_DELAY}ms (activeUsers: ${activeUsers})`);

    reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        stompClient = null;
        _doConnect();
    }, RECONNECT_DELAY);
};

/**
 * Инициализация WebSocket подключения (внешний вызов от компонентов)
 * @param {Function} onMessageCallback - Колбак для обработки входящих сообщений
 * @param {Function} onTypingCallback - Колбак для обработки событий набора текста
 * @param {string} token - JWT токен (опционально, берётся из token.js если не передан)
 */
export const initWebSocket = (onMessageCallback, onTypingCallback, token = null) => {
    if (onMessageCallback || onTypingCallback) {
        globalCallbacks = { onMessage: onMessageCallback, onTyping: onTypingCallback };
    }
    if (token) {
        globalToken = token;
    }

    activeUsers++;
    console.log(`[WebSocket] Подключение запрошено (activeUsers: ${activeUsers})`);

    // Если уже подключены — просто уведомляем
    if (stompClient && (isConnected || stompClient.active)) {
        console.log('[WebSocket] Уже подключён, уведомляем колбэк');
        if (typeof onMessageCallback === 'function') {
            onMessageCallback({ connected: true, client: stompClient });
        }
        return;
    }

    // Сбрасываем счётчик попыток при новом внешнем запросе, если не подключены
    if (!isConnected) {
        reconnectAttempts = 0;
    }

    // Отменяем старый таймер reconnect, если был
    if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
    }

    _doConnect();
};

/**
 * Отключение от WebSocket (reference counting)
 * WebSocket отключается только когда последний компонент вызвал disconnect
 */
export const disconnectWebSocket = () => {
    activeUsers = Math.max(0, activeUsers - 1);
    console.log(`[WebSocket] Отключение запрошено (activeUsers: ${activeUsers})`);

    if (activeUsers > 0) {
        // Ещё есть компоненты, использующие WebSocket — не отключаем
        return;
    }

    // Последний пользователь отписался — отключаем
    if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
    }

    if (stompClient) {
        // Отменяем все подписки перед отключением
        subscriptions.forEach(sub => {
            try {
                sub.subscription.unsubscribe();
            } catch (e) {
                console.error('[WebSocket] Ошибка отписки при отключении:', e);
            }
        });
        subscriptions = [];

        stompClient.deactivate();
        console.log('[WebSocket] Полностью отключено');
        isConnected = false;
        stompClient = null;
    }

    globalCallbacks = null;
    globalToken = null;
    reconnectAttempts = 0;
};

/**
 * Принудительное отключение WebSocket (например, при logout)
 */
export const forceDisconnectWebSocket = () => {
    activeUsers = 0;
    if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
    }
    if (stompClient) {
        // Отменяем все подписки
        subscriptions.forEach(sub => {
            try {
                sub.subscription.unsubscribe();
            } catch (e) {
                console.error('[WebSocket] Ошибка отписки при принудительном отключении:', e);
            }
        });
        subscriptions = [];

        stompClient.deactivate();
        isConnected = false;
        stompClient = null;
    }
    globalCallbacks = null;
    globalToken = null;
    reconnectAttempts = 0;
    console.log('[WebSocket] Принудительно отключено');
};

/**
 * Проверка статуса подключения
 */
export const isWebSocketConnected = () => {
    return isConnected && stompClient && stompClient.active;
};

/**
 * Отправка сообщения в чат
 * @param {string} chatId - ID чата
 * @param {string|Object} content - Текст сообщения или объект { content, replyToMessageId }
 * @returns {boolean} true если сообщение отправлено
 */
export const sendMessage = (chatId, content) => {
    if (!isWebSocketConnected()) {
        return false;
    }

    try {
        // V2: MessageWebSocketRequest { content, replyToMessageId? }
        const payload = typeof content === 'string'
            ? { content }
            : { content: content.content, replyToMessageId: content.replyToMessageId || null };

        stompClient.publish({
            destination: `/app/chat.message.${chatId}`,
            body: JSON.stringify(payload)
        });
        return true;
    } catch (error) {
        return false;
    }
};

/**
 * Отправка события "печатает..."
 * V2: TypingWebSocketRequest { isTyping: Boolean? } — по умолчанию true
 * Сервер сам резолвит профиль из аутентификации и формирует TypingWebSocketEvent.
 * @param {string} chatId - ID чата
 * @param {Object} [userData] - (legacy, V2 игнорирует — сервер резолвит сам)
 */
export const sendTyping = (chatId, userData = {}, isTyping = true) => {
    if (!isWebSocketConnected()) {
        return false;
    }

    try {
        stompClient.publish({
            destination: `/app/chat.typing.${chatId}`,
            body: JSON.stringify({
                isTyping,
                profileId: userData.profileId || null,
                displayName: userData.displayName || null,
            })
        });
        return true;
    } catch (error) {
        console.error('[WebSocket] Ошибка отправки typing события:', error);
        return false;
    }
};

/**
 * Подписка на сообщения чата
 * @param {string} chatId - ID чата
 * @param {Function} callback - Колбак для обработки сообщений
 * @returns {Object} Subscription объект для отписки
 */
export const subscribeToChat = (chatId, callback) => {
    if (!isWebSocketConnected()) {
        return null;
    }

    try {
        const subscription = stompClient.subscribe(`/topic/chat.${chatId}`, (message) => {
            try {
                const data = JSON.parse(message.body);
                callback(data);
            } catch (error) {
                console.error('[WebSocket] Ошибка парсинга сообщения:', error);
            }
        });

        // Регистрируем подписку для восстановления после reconnect
        _registerSubscription(subscription, 'chat', chatId, null, callback);

        return {
            unsubscribe: () => {
                _unregisterSubscription(subscription);
                subscription.unsubscribe();
            }
        };
    } catch (error) {
        return null;
    }
};

/**
 * Подписка на события набора текста
 * @param {string} chatId - ID чата
 * @param {Function} callback - Колбак для обработки событий
 * @returns {Object} Subscription объект для отписки
 */
export const subscribeToTyping = (chatId, callback) => {
    if (!isWebSocketConnected()) {
        console.error('[WebSocket] Не подключён к WebSocket');
        return null;
    }

    try {
        const subscription = stompClient.subscribe(`/topic/chat.${chatId}.typing`, (event) => {
            try {
                const data = JSON.parse(event.body);
                callback(data);
            } catch (error) {
                console.error('[WebSocket] Ошибка парсинга typing события:', error);
            }
        });

        console.log('[WebSocket] Подписка на typing:', chatId);

        // Регистрируем подписку для восстановления после reconnect
        _registerSubscription(subscription, 'typing', chatId, null, callback);

        return {
            unsubscribe: () => {
                _unregisterSubscription(subscription);
                subscription.unsubscribe();
            }
        };
    } catch (error) {
        console.error('[WebSocket] Ошибка подписки на typing:', error);
        return null;
    }
};

/**
 * Подписка на события закрепления/открепления сообщений
 * @param {string} chatId - ID чата
 * @param {Function} callback - Колбэк для обработки событий PIN/UNPIN
 * @returns {Object} Subscription объект для отписки
 */
export const subscribeToPinned = (chatId, callback) => {
    if (!isWebSocketConnected()) {
        console.error('[WebSocket] Не подключён к WebSocket');
        return null;
    }

    try {
        const subscription = stompClient.subscribe(`/topic/chat.${chatId}.pinned`, (event) => {
            try {
                const data = JSON.parse(event.body);
                callback(data);
            } catch (error) {
                console.error('[WebSocket] Ошибка парсинга pinned события:', error);
            }
        });

        console.log('[WebSocket] Подписка на pinned:', chatId);

        _registerSubscription(subscription, 'pinned', chatId, null, callback);

        return {
            unsubscribe: () => {
                _unregisterSubscription(subscription);
                subscription.unsubscribe();
            }
        };
    } catch (error) {
        console.error('[WebSocket] Ошибка подписки на pinned:', error);
        return null;
    }
};

/**
 * Вход в чат (уведомление другим пользователям) * @param {string} chatId - ID чата
 */
export const joinChat = (chatId) => {
    if (!isWebSocketConnected()) {
        return false;
    }

    try {
        stompClient.publish({
            destination: `/app/chat.join.${chatId}`,
            body: JSON.stringify({})
        });
        // Логирование отключено для уменьшения шума
        return true;
    } catch (error) {
        console.error('[WebSocket] Ошибка входа в чат:', error);
        return false;
    }
};

/**
 * Выход из чата
 * @param {string} chatId - ID чата
 */
export const leaveChat = (chatId) => {
    if (!isWebSocketConnected()) {
        return false;
    }

    try {
        stompClient.publish({
            destination: `/app/chat.leave.${chatId}`,
            body: JSON.stringify({})
        });
        // Логирование отключено для уменьшения шума
        return true;
    } catch (error) {
        console.error('[WebSocket] Ошибка выхода из чата:', error);
        return false;
    }
};

/**
 * Подписка на обновления списка чатов (новые чаты, изменения)
 * @param {Function} callback - Колбак для обработки обновлений
 * @returns {Object} Subscription объект для отписки
 */
export const subscribeToChatUpdates = (callback) => {
    if (!isWebSocketConnected()) {
        console.error('[WebSocket] Не подключён к WebSocket');
        return null;
    }

    try {
        const subscription = stompClient.subscribe(`/topic/chat.updates`, (event) => {
            try {
                const data = JSON.parse(event.body);
                console.log('[WebSocket] Chat update event:', data);
                if (typeof callback === 'function') {
                    callback(data);
                }
            } catch (error) {
                console.error('[WebSocket] Ошибка парсинга chat update события:', error);
            }
        });

        console.log('[WebSocket] Подписка на обновления чатов');
        return {
            unsubscribe: () => subscription.unsubscribe()
        };
    } catch (error) {
        console.error('[WebSocket] Ошибка подписки на обновления чатов:', error);
        return null;
    }
};

/**
 * Подписка на обновления онлайн-статусов всех пользователей
 * @param {Function} callback - Колбак для обработки обновлений
 * @returns {Object} Subscription объект для отписки
 */
export const subscribeToPresenceUpdates = (callback) => {
    if (!isWebSocketConnected()) {
        console.error('[WebSocket] Не подключён к WebSocket');
        return null;
    }

    try {
        const subscription = stompClient.subscribe('/topic/presence.updates', (event) => {
            try {
                const data = JSON.parse(event.body);
                console.log('[WebSocket] Presence update:', data);
                if (typeof callback === 'function') {
                    callback(data);
                }
            } catch (error) {
                console.error('[WebSocket] Ошибка парсинга presence события:', error);
            }
        });

        console.log('[WebSocket] Подписка на presence.updates');
        return {
            unsubscribe: () => subscription.unsubscribe()
        };
    } catch (error) {
        console.error('[WebSocket] Ошибка подписки на presence.updates:', error);
        return null;
    }
};

/**
 * Подписка на онлайн-статус конкретного пользователя
 * @param {string} userId - ID пользователя
 * @param {Function} callback - Колбак для обработки обновлений
 * @returns {Object} Subscription объект для отписки
 */
export const subscribeToUserPresence = (userId, callback) => {
    if (!isWebSocketConnected()) {
        console.error('[WebSocket] Не подключён к WebSocket');
        return null;
    }

    try {
        const subscription = stompClient.subscribe(`/topic/presence.${userId}`, (event) => {
            try {
                const data = JSON.parse(event.body);
                console.log('[WebSocket] User presence update:', data);
                if (typeof callback === 'function') {
                    callback(data);
                }
            } catch (error) {
                console.error('[WebSocket] Ошибка парсинга user presence события:', error);
            }
        });

        console.log('[WebSocket] Подписка на presence пользователя:', userId);

        // Регистрируем подписку для восстановления после reconnect
        _registerSubscription(subscription, 'presence', null, userId, callback);

        return {
            unsubscribe: () => {
                _unregisterSubscription(subscription);
                subscription.unsubscribe();
            }
        };
    } catch (error) {
        console.error('[WebSocket] Ошибка подписки на presence пользователя:', error);
        return null;
    }
};

/**
 * Подписка на обновления дуэли
 * @param {string} duelId - ID дуэли
 * @param {Function} callback - Колбак для обработки событий дуэли
 * @returns {Object} Subscription объект для отписки
 */
export const subscribeToDuel = (duelId, callback) => {
    if (!isWebSocketConnected()) {
        console.error('[WebSocket] Не подключён к WebSocket');
        return null;
    }

    try {
        const subscription = stompClient.subscribe(`/topic/duel.${duelId}`, (event) => {
            try {
                const data = JSON.parse(event.body);
                console.log('[WebSocket] Duel event:', data);
                if (typeof callback === 'function') {
                    callback(data);
                }
            } catch (error) {
                console.error('[WebSocket] Ошибка парсинга duel события:', error);
            }
        });

        console.log('[WebSocket] Подписка на duel:', duelId);
        return {
            unsubscribe: () => subscription.unsubscribe()
        };
    } catch (error) {
        console.error('[WebSocket] Ошибка подписки на duel:', error);
        return null;
    }
};

/**
 * Подписка на персональные уведомления дуэлей
 * @param {Function} callback - Колбак для обработки уведомлений
 * @returns {Object} Subscription объект для отписки
 */
export const subscribeToDuelNotifications = (callback) => {
    if (!isWebSocketConnected()) {
        console.error('[WebSocket] Не подключён к WebSocket');
        return null;
    }

    try {
        const subscription = stompClient.subscribe('/user/queue/duel', (event) => {
            try {
                const data = JSON.parse(event.body);
                console.log('[WebSocket] Duel notification:', data);
                if (typeof callback === 'function') {
                    callback(data);
                }
            } catch (error) {
                console.error('[WebSocket] Ошибка парсинга duel notification:', error);
            }
        });

        console.log('[WebSocket] Подписка на duel notifications');
        return {
            unsubscribe: () => subscription.unsubscribe()
        };
    } catch (error) {
        console.error('[WebSocket] Ошибка подписки на duel notifications:', error);
        return null;
    }
};

/**
 * Подписка на общие уведомления (NEW_COMMENT, NEW_MESSAGE, BATTLE_STARTED и т.д.)
 * Сервер отправляет уведомления в /user/queue/notifications
 *
 * @param {Function} callback - Колбэк с данными уведомления
 * @returns {Object|null} { unsubscribe: Function }
 */
export const subscribeToNotifications = (callback) => {
    if (!isWebSocketConnected()) {
        console.error('[WebSocket] Не подключён к WebSocket');
        return null;
    }

    try {
        const subscription = stompClient.subscribe('/user/queue/notifications', (event) => {
            try {
                const data = JSON.parse(event.body);
                console.log('[WebSocket] Уведомление:', data.type, data.title);
                if (typeof callback === 'function') {
                    callback(data);
                }
            } catch (error) {
                console.error('[WebSocket] Ошибка парсинга уведомления:', error);
            }
        });

        // Регистрируем для восстановления после reconnect
        _registerSubscription(subscription, 'notifications', null, null, callback);

        console.log('[WebSocket] Подписка на уведомления /user/queue/notifications');
        return {
            unsubscribe: () => {
                _unregisterSubscription(subscription);
                subscription.unsubscribe();
            }
        };
    } catch (error) {
        console.error('[WebSocket] Ошибка подписки на уведомления:', error);
        return null;
    }
};

/**
 * Подписка на обновления рейтинга
 * @param {Function} callback - Колбак для обработки обновлений рейтинга
 * @returns {Object} Subscription объект для отписки
 */
export const subscribeToRatingUpdates = (callback) => {
    if (!isWebSocketConnected()) {
        console.error('[WebSocket] Не подключён к WebSocket');
        return null;
    }

    try {
        const subscription = stompClient.subscribe('/user/queue/rating', (event) => {
            try {
                const data = JSON.parse(event.body);
                console.log('[WebSocket] Rating update:', data);
                if (typeof callback === 'function') {
                    callback(data);
                }
            } catch (error) {
                console.error('[WebSocket] Ошибка парсинга rating update:', error);
            }
        });

        console.log('[WebSocket] Подписка на rating updates');
        return {
            unsubscribe: () => subscription.unsubscribe()
        };
    } catch (error) {
        console.error('[WebSocket] Ошибка подписки на rating updates:', error);
        return null;
    }
};

/**
 * Подписка на обновления турнира
 * @param {string} tournamentId - ID турнира
 * @param {Function} callback - Колбак для обработки событий турнира
 * @returns {Object} Subscription объект для отписки
 */
export const subscribeToTournament = (tournamentId, callback) => {
    if (!isWebSocketConnected()) {
        console.error('[WebSocket] Не подключён к WebSocket');
        return null;
    }

    try {
        const subscription = stompClient.subscribe(`/topic/tournament.${tournamentId}`, (event) => {
            try {
                const data = JSON.parse(event.body);
                console.log('[WebSocket] Tournament event:', data);
                if (typeof callback === 'function') {
                    callback(data);
                }
            } catch (error) {
                console.error('[WebSocket] Ошибка парсинга tournament события:', error);
            }
        });

        console.log('[WebSocket] Подписка на tournament:', tournamentId);
        return {
            unsubscribe: () => subscription.unsubscribe()
        };
    } catch (error) {
        console.error('[WebSocket] Ошибка подписки на tournament:', error);
        return null;
    }
};

/**
 * Подписка на события баттла
 * @param {string} battleId - ID баттла
 * @param {Function} callback - Колбак для обработки событий баттла
 * @returns {Object} Subscription объект для отписки
 */
export const subscribeToBattle = (battleId, callback) => {
    if (!isWebSocketConnected()) {
        console.error('[WebSocket] Не подключён к WebSocket');
        return null;
    }

    try {
        const subscription = stompClient.subscribe(`/topic/battle.${battleId}`, (event) => {
            try {
                const data = JSON.parse(event.body);
                console.log('[WebSocket] Battle event:', data);
                if (typeof callback === 'function') {
                    callback(data);
                }
            } catch (error) {
                console.error('[WebSocket] Ошибка парсинга battle события:', error);
            }
        });

        console.log('[WebSocket] Подписка на battle:', battleId);
        return {
            unsubscribe: () => subscription.unsubscribe()
        };
    } catch (error) {
        console.error('[WebSocket] Ошибка подписки на battle:', error);
        return null;
    }
};

/**
 * Подписка на события раунда
 * @param {string} roundId - ID раунда
 * @param {Function} callback - Колбак для обработки событий раунда
 * @returns {Object} Subscription объект для отписки
 */
export const subscribeToRound = (roundId, callback) => {
    if (!isWebSocketConnected()) {
        console.error('[WebSocket] Не подключён к WebSocket');
        return null;
    }

    try {
        const subscription = stompClient.subscribe(`/topic/round.${roundId}`, (event) => {
            try {
                const data = JSON.parse(event.body);
                console.log('[WebSocket] Round event:', data);
                if (typeof callback === 'function') {
                    callback(data);
                }
            } catch (error) {
                console.error('[WebSocket] Ошибка парсинга round события:', error);
            }
        });

        console.log('[WebSocket] Подписка на round:', roundId);
        return {
            unsubscribe: () => subscription.unsubscribe()
        };
    } catch (error) {
        console.error('[WebSocket] Ошибка подписки на round:', error);
        return null;
    }
};

/**
 * Подписка на события пар раунда (Battle Pairs)
 * @param {string} roundId - ID раунда
 * @param {Function} callback - Колбак для обработки событий пар
 * @returns {Object} Subscription объект для отписки
 */
export const subscribeToBattlePairRound = (roundId, callback) => {
    if (!isWebSocketConnected()) {
        console.error('[WebSocket] Не подключён к WebSocket');
        return null;
    }

    try {
        const subscription = stompClient.subscribe(`/topic/battle_pairs.round.${roundId}`, (event) => {
            try {
                const data = JSON.parse(event.body);
                console.log('[WebSocket] BattlePair round event:', data);
                if (typeof callback === 'function') {
                    callback(data);
                }
            } catch (error) {
                console.error('[WebSocket] Ошибка парсинга battlePair события:', error);
            }
        });

        console.log('[WebSocket] Подписка на battle_pairs.round:', roundId);
        return {
            unsubscribe: () => subscription.unsubscribe()
        };
    } catch (error) {
        console.error('[WebSocket] Ошибка подписки на battle_pairs.round:', error);
        return null;
    }
};

/**
 * Подписка на события конкретной пары
 * @param {string} pairId - ID пары
 * @param {Function} callback - Колбак для обработки событий пары
 * @returns {Object} Subscription объект для отписки
 */
export const subscribeToBattlePair = (pairId, callback) => {
    if (!isWebSocketConnected()) {
        console.error('[WebSocket] Не подключён к WebSocket');
        return null;
    }

    try {
        const subscription = stompClient.subscribe(`/topic/battle_pairs.pair.${pairId}`, (event) => {
            try {
                const data = JSON.parse(event.body);
                console.log('[WebSocket] BattlePair event:', data);
                if (typeof callback === 'function') {
                    callback(data);
                }
            } catch (error) {
                console.error('[WebSocket] Ошибка парсинга battlePair события:', error);
            }
        });

        console.log('[WebSocket] Подписка на battle_pairs.pair:', pairId);
        return {
            unsubscribe: () => subscription.unsubscribe()
        };
    } catch (error) {
        console.error('[WebSocket] Ошибка подписки на battle_pairs.pair:', error);
        return null;
    }
};

/**
 * Подписка на Live чат баттла
 * V2: топик /topic/battle.{battleId}.chat
 * @param {string} battleId - ID баттла
 * @param {Function} callback - Колбак для обработки сообщений
 * @returns {Object} Subscription объект для отписки
 */
export const subscribeToBattleLiveChat = (battleId, callback) => {
    if (!isWebSocketConnected()) {
        console.error('[WebSocket] Не подключён к WebSocket');
        return null;
    }

    try {
        const subscription = stompClient.subscribe(`/topic/battle.${battleId}.chat`, (message) => {
            try {
                const data = JSON.parse(message.body);
                if (typeof callback === 'function') {
                    callback(data);
                }
            } catch (error) {
                console.error('[WebSocket] Ошибка парсинга сообщения battle live chat:', error);
            }
        });

        return {
            unsubscribe: () => subscription.unsubscribe()
        };
    } catch (error) {
        console.error('[WebSocket] Ошибка подписки на battle live chat:', error);
        return null;
    }
};

/**
 * Подписка на чат участников баттла
 * V2: топик /topic/battle.{battleId}.participants.chat
 * @param {string} battleId - ID баттла
 * @param {Function} callback - Колбак для обработки сообщений
 * @returns {Object} Subscription объект для отписки
 */
export const subscribeToBattleParticipantsChat = (battleId, callback) => {
    if (!isWebSocketConnected()) {
        console.error('[WebSocket] Не подключён к WebSocket');
        return null;
    }

    try {
        const subscription = stompClient.subscribe(`/topic/battle.${battleId}.participants.chat`, (message) => {
            try {
                const data = JSON.parse(message.body);
                if (typeof callback === 'function') {
                    callback(data);
                }
            } catch (error) {
                console.error('[WebSocket] Ошибка парсинга сообщения battle participants chat:', error);
            }
        });

        return {
            unsubscribe: () => subscription.unsubscribe()
        };
    } catch (error) {
        console.error('[WebSocket] Ошибка подписки на battle participants chat:', error);
        return null;
    }
};

/**
 * Подписка на события набора текста в Live-чате баттла
 * V2: топик /topic/battle.{battleId}.chat.typing
 * @param {string} battleId - ID баттла
 * @param {Function} callback - Колбак для обработки событий
 * @returns {Object} Subscription объект для отписки
 */
export const subscribeToBattleChatTyping = (battleId, callback) => {
    if (!isWebSocketConnected()) {
        console.error('[WebSocket] Не подключён к WebSocket');
        return null;
    }

    try {
        const subscription = stompClient.subscribe(`/topic/battle.${battleId}.chat.typing`, (event) => {
            try {
                const data = JSON.parse(event.body);
                if (typeof callback === 'function') {
                    callback(data);
                }
            } catch (error) {
                console.error('[WebSocket] Ошибка парсинга battle typing события:', error);
            }
        });

        return {
            unsubscribe: () => subscription.unsubscribe()
        };
    } catch (error) {
        console.error('[WebSocket] Ошибка подписки на battle typing:', error);
        return null;
    }
};

/**
 * Отправка сообщения в Live чат баттла
 * V2: destination /app/battle.{battleId}.chat.message
 * @param {string} battleId - ID баттла
 * @param {string} content - Текст сообщения
 * @param {string} [replyToMessageId] - ID сообщения для ответа (UUID)
 * @returns {boolean} true если сообщение отправлено
 */
export const sendBattleLiveChatMessage = (battleId, content, replyToMessageId = null) => {
    if (!isWebSocketConnected()) {
        console.error('[WebSocket] Не подключён к WebSocket');
        return false;
    }

    try {
        stompClient.publish({
            destination: `/app/battle.${battleId}.chat.message`,
            body: JSON.stringify({ content, replyToMessageId })
        });
        return true;
    } catch (error) {
        console.error('[WebSocket] Ошибка отправки сообщения в battle live chat:', error);
        return false;
    }
};

/**
 * Отправка сообщения в чат участников баттла
 * V2: destination /app/battle.{battleId}.participants.chat.message
 * @param {string} battleId - ID баттла
 * @param {string} content - Текст сообщения
 * @param {string} [replyToMessageId] - ID сообщения для ответа (UUID)
 * @returns {boolean} true если сообщение отправлено
 */
export const sendBattleParticipantsChatMessage = (battleId, content, replyToMessageId = null) => {
    if (!isWebSocketConnected()) {
        console.error('[WebSocket] Не подключён к WebSocket');
        return false;
    }

    try {
        stompClient.publish({
            destination: `/app/battle.${battleId}.participants.chat.message`,
            body: JSON.stringify({ content, replyToMessageId })
        });
        return true;
    } catch (error) {
        console.error('[WebSocket] Ошибка отправки сообщения в battle participants chat:', error);
        return false;
    }
};

/**
 * Отправка события набора текста в Live-чат баттла
 * V2: destination /app/battle.{battleId}.chat.typing
 * @param {string} battleId - ID баттла
 * @returns {boolean} true если событие отправлено
 */
export const sendBattleChatTyping = (battleId) => {
    if (!isWebSocketConnected()) {
        return false;
    }

    try {
        stompClient.publish({
            destination: `/app/battle.${battleId}.chat.typing`,
            body: JSON.stringify({})
        });
        return true;
    } catch (error) {
        console.error('[WebSocket] Ошибка отправки battle typing:', error);
        return false;
    }
};

/**
 * Подключение к Live-чату баттла
 * V2: destination /app/battle.{battleId}.chat.join
 * @param {string} battleId - ID баттла
 * @returns {boolean}
 */
export const joinBattleChat = (battleId) => {
    if (!isWebSocketConnected()) {
        return false;
    }

    try {
        stompClient.publish({
            destination: `/app/battle.${battleId}.chat.join`,
            body: JSON.stringify({})
        });
        return true;
    } catch (error) {
        console.error('[WebSocket] Ошибка входа в battle chat:', error);
        return false;
    }
};

/**
 * Отключение от Live-чата баттла
 * V2: destination /app/battle.{battleId}.chat.leave
 * @param {string} battleId - ID баттла
 * @returns {boolean}
 */
export const leaveBattleChat = (battleId) => {
    if (!isWebSocketConnected()) {
        return false;
    }

    try {
        stompClient.publish({
            destination: `/app/battle.${battleId}.chat.leave`,
            body: JSON.stringify({})
        });
        return true;
    } catch (error) {
        console.error('[WebSocket] Ошибка выхода из battle chat:', error);
        return false;
    }
};

/**
 * Подписка на обновления опросов в чате
 * Топик /topic/chat.{chatId}.polls — события голосования/закрытия опросов
 * @param {string} chatId - ID чата
 * @param {Function} callback - Колбэк для обработки событий опроса
 * @returns {Object} Subscription объект для отписки
 */
export const subscribeToPolls = (chatId, callback) => {
    if (!isWebSocketConnected()) {
        console.error('[WebSocket] Не подключён к WebSocket');
        return null;
    }

    try {
        const subscription = stompClient.subscribe(`/topic/chat.${chatId}.polls`, (event) => {
            try {
                const data = JSON.parse(event.body);
                if (typeof callback === 'function') {
                    callback(data);
                }
            } catch (error) {
                console.error('[WebSocket] Ошибка парсинга poll события:', error);
            }
        });

        console.log('[WebSocket] Подписка на polls:', chatId);

        _registerSubscription(subscription, 'polls', chatId, null, callback);

        return {
            unsubscribe: () => {
                _unregisterSubscription(subscription);
                subscription.unsubscribe();
            }
        };
    } catch (error) {
        console.error('[WebSocket] Ошибка подписки на polls:', error);
        return null;
    }
};

/**
 * Подписка на ошибки WebSocket
 * V2: топик /user/queue/errors → WebSocketErrorResponse { error, message }
 * @param {Function} callback - Колбак для обработки ошибок
 * @returns {Object} Subscription объект для отписки
 */
export const subscribeToWebSocketErrors = (callback) => {
    if (!isWebSocketConnected()) {
        console.error('[WebSocket] Не подключён к WebSocket');
        return null;
    }

    try {
        const subscription = stompClient.subscribe('/user/queue/errors', (event) => {
            try {
                const data = JSON.parse(event.body);
                if (typeof callback === 'function') {
                    callback(data);
                }
            } catch (error) {
                console.error('[WebSocket] Ошибка парсинга error события:', error);
            }
        });

        return {
            unsubscribe: () => subscription.unsubscribe()
        };
    } catch (error) {
        console.error('[WebSocket] Ошибка подписки на errors:', error);
        return null;
    }
};

/**
 * Подписка на персональные события очереди Quick Duel
 * @param {Function} callback - Колбак для обработки событий очереди
 * @returns {Object} Subscription объект для отписки
 */
export const subscribeToQuickDuelQueue = (callback) => {
    if (!isWebSocketConnected()) {
        console.error('[WebSocket] Не подключён к WebSocket');
        return null;
    }

    try {
        const subscription = stompClient.subscribe('/user/queue/quick_duel', (event) => {
            try {
                const data = JSON.parse(event.body);
                console.log('[WebSocket] Quick Duel queue event:', data);
                if (typeof callback === 'function') {
                    callback(data);
                }
            } catch (error) {
                console.error('[WebSocket] Ошибка парсинга Quick Duel события:', error);
            }
        });

        console.log('[WebSocket] Подписка на /user/queue/quick_duel');

        // Регистрируем подписку для восстановления после reconnect
        subscriptions.push({
            type: 'quickDuelQueue',
            callback,
            subscription
        });

        return {
            unsubscribe: () => {
                subscription.unsubscribe();
                const index = subscriptions.findIndex(s => s.type === 'quickDuelQueue' && s.subscription === subscription);
                if (index !== -1) {
                    subscriptions.splice(index, 1);
                }
            }
        };
    } catch (error) {
        console.error('[WebSocket] Ошибка подписки на Quick Duel queue:', error);
        return null;
    }
};

/**
 * Подписка на публичную статистику очереди Quick Duel
 * @param {Function} callback - Колбак для обработки статистики
 * @returns {Object} Subscription объект для отписки
 */
export const subscribeToQuickDuelStats = (callback) => {
    if (!isWebSocketConnected()) {
        console.error('[WebSocket] Не подключён к WebSocket');
        return null;
    }

    try {
        const subscription = stompClient.subscribe('/topic/quick_duel.queue.stats', (event) => {
            try {
                const data = JSON.parse(event.body);
                console.log('[WebSocket] Quick Duel stats:', data);
                if (typeof callback === 'function') {
                    callback(data);
                }
            } catch (error) {
                console.error('[WebSocket] Ошибка парсинга Quick Duel статистики:', error);
            }
        });

        console.log('[WebSocket] Подписка на /topic/quick_duel.queue.stats');

        // Регистрируем подписку для восстановления после reconnect
        subscriptions.push({
            type: 'quickDuelStats',
            callback,
            subscription
        });

        return {
            unsubscribe: () => {
                subscription.unsubscribe();
                const index = subscriptions.findIndex(s => s.type === 'quickDuelStats' && s.subscription === subscription);
                if (index !== -1) {
                    subscriptions.splice(index, 1);
                }
            }
        };
    } catch (error) {
        console.error('[WebSocket] Ошибка подписки на Quick Duel stats:', error);
        return null;
    }
};

/**
 * Отправить подключение к очереди Quick Duel
 * @returns {boolean} true если команда отправлена
 */
export const joinQuickDuelQueue = () => {
    if (!isWebSocketConnected()) {
        console.error('[WebSocket] Не подключён к WebSocket');
        return false;
    }

    try {
        stompClient.publish({
            destination: '/app/quick_duel.join',
            body: JSON.stringify({})
        });
        console.log('[WebSocket] Отправлено подключение к Quick Duel очереди');
        return true;
    } catch (error) {
        console.error('[WebSocket] Ошибка подключения к Quick Duel очереди:', error);
        return false;
    }
};

/**
 * Отправить отключение от очереди Quick Duel
 * @returns {boolean} true если команда отправлена
 */
export const leaveQuickDuelQueue = () => {
    if (!isWebSocketConnected()) {
        console.error('[WebSocket] Не подключён к WebSocket');
        return false;
    }

    try {
        stompClient.publish({
            destination: '/app/quick_duel.leave',
            body: JSON.stringify({})
        });
        console.log('[WebSocket] Отправлено отключение от Quick Duel очереди');
        return true;
    } catch (error) {
        console.error('[WebSocket] Ошибка отключения от Quick Duel очереди:', error);
        return false;
    }
};

export default {
    init: initWebSocket,
    disconnect: disconnectWebSocket,
    forceDisconnect: forceDisconnectWebSocket,
    isConnected: isWebSocketConnected,
    sendMessage,
    sendTyping,
    subscribeToChat,
    subscribeToTyping,
    subscribeToPinned,
    subscribeToPolls,
    subscribeToChatUpdates,
    subscribeToPresenceUpdates,
    subscribeToUserPresence,
    subscribeToDuel,
    subscribeToDuelNotifications,
    subscribeToNotifications,
    subscribeToRatingUpdates,
    subscribeToTournament,
    subscribeToBattle,
    subscribeToRound,
    subscribeToBattlePairRound,
    subscribeToBattlePair,
    subscribeToBattleLiveChat,
    subscribeToBattleParticipantsChat,
    subscribeToBattleChatTyping,
    subscribeToWebSocketErrors,
    subscribeToQuickDuelQueue,
    subscribeToQuickDuelStats,
    sendBattleLiveChatMessage,
    sendBattleParticipantsChatMessage,
    sendBattleChatTyping,
    joinBattleChat,
    leaveBattleChat,
    joinQuickDuelQueue,
    leaveQuickDuelQueue,
    joinChat,
    leaveChat
};


